import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal } from 'ui/styled';
import { pathOr } from 'ramda';
import { LogoutOutlined, PhoneOutlined } from '@ant-design/icons';
import { useNicService } from 'stores/nic/useNicService';
import { useCallStore } from 'stores/nic/stores/callStore';
import { selectAgents, useContactStore } from 'stores/nic/stores/contactStore';
import { useCallActionsConfig } from 'pages/NIC/hooks/useCallActionsConfig';
import { DirectoryEntities } from '@nice-devone/common-sdk';
import { useMembersForNicQuery } from 'apollo/queries/NIC/MEMBERS_FOR_NIC';
import { getNodes } from 'utils/paginationHelper';
import { isValidPhoneNumber } from 'libphonenumber-js';
import DTMFKeypad from 'pages/NIC/components/DTMFKeypad';
import { StyledMember, CallAction, StyledCallFooter, EndCallButton, LeaveCallButton } from './styled';
export var ACTION_AVAILABILITY = {
    hold: ['Active', 'Holding', 'Joined', 'Conferenced'],
    mute: ['Active', 'Joined', 'Conferenced'],
    transfer: ['Active'],
    record: ['Active', 'Joined', 'Conferenced'],
    mask: ['Active', 'Joined', 'Conferenced'],
    end: ['Ringing', 'Active', 'Holding', 'Joined', 'Conferenced']
};
var canShowAction = function (actionKey, status) {
    var allowedStatuses = ACTION_AVAILABILITY[actionKey] || [];
    return allowedStatuses.includes(status);
};
var CallInProgress = function (_a) {
    var _b, _c;
    var callIndex = _a.callIndex;
    var callEntry = useCallStore(function (state) { return state.calls[callIndex]; });
    var call = callEntry === null || callEntry === void 0 ? void 0 : callEntry.call;
    var memberToCall = callEntry === null || callEntry === void 0 ? void 0 : callEntry.memberInfo;
    var agentToCall = callEntry === null || callEntry === void 0 ? void 0 : callEntry.agentInfo;
    var isOnHold = (_b = callEntry === null || callEntry === void 0 ? void 0 : callEntry.isOnHold) !== null && _b !== void 0 ? _b : false;
    var isMuted = (_c = callEntry === null || callEntry === void 0 ? void 0 : callEntry.isMuted) !== null && _c !== void 0 ? _c : false;
    var agents = useContactStore(selectAgents);
    var setCalls = useCallStore(function (state) { return state.setCalls; });
    var _d = useNicService(), endCall = _d.endCall, loadDirectoryData = _d.loadDirectoryData, startConferenceCall = _d.startConferenceCall, leaveConferenceCall = _d.leaveConferenceCall;
    var isInbound = call === null || call === void 0 ? void 0 : call.isInbound;
    var ani = (call === null || call === void 0 ? void 0 : call.ani) || '';
    var dnis = (call === null || call === void 0 ? void 0 : call.dnis) || '';
    var callerId = isInbound ? ani : dnis;
    var validPhoneNumber = [ani, dnis].find(function (num) { return isValidPhoneNumber(num); }) || null;
    var isConferenceActive = useCallStore(function (state) { return state.isConferenceActive; });
    var isConferenceInitiated = useCallStore(function (state) { return state.isConferenceInitiated; });
    var setConferenceInitiated = useCallStore(function (state) { return state.setConferenceInitiated; });
    var activeCallsCount = useCallStore(function (state) { return state.calls.filter(function (c) { return c.call !== null; }).length; });
    var isFirstActiveCall = useCallStore(function (state) { return state.calls.findIndex(function (c) { return c.call !== null; }) === callIndex; });
    var showAdditionalDirectory = useCallStore(function (state) { return state.showAdditionalDirectory; });
    var callActionsConfig = useCallActionsConfig(callIndex);
    useEffect(function () {
        if (agentToCall || memberToCall)
            return;
        // Only load directory data on the tab that owns the call
        var tabId = sessionStorage.getItem('tabId');
        var activeTabId = localStorage.getItem('activeCallTabId');
        if (tabId !== activeTabId)
            return;
        var cleanup = loadDirectoryData({
            entity: [
                DirectoryEntities.AGENT_LIST,
                DirectoryEntities.SKILL_LIST,
                DirectoryEntities.TEAM_LIST
            ]
        });
        return function () { return cleanup && cleanup(); };
    }, [loadDirectoryData, agentToCall, memberToCall]);
    var agentContact = useMemo(function () {
        return !agentToCall
            ? agents === null || agents === void 0 ? void 0 : agents.find(function (item) { var _a; return ((_a = item.agentId) === null || _a === void 0 ? void 0 : _a.toString()) === callerId; })
            : null;
    }, [agents, agentToCall, callerId]);
    useEffect(function () {
        if (agentContact) {
            setCalls({ index: callIndex, agentInfo: agentContact });
        }
    }, [agentContact, callIndex, setCalls]);
    var shouldFetchMember = !memberToCall && !agentToCall && !agentContact;
    var memberData = useMembersForNicQuery({
        variables: { filters: { phone_search: callerId } },
        skip: !shouldFetchMember
    }).data;
    var fetchedMembers = useMemo(function () { return (shouldFetchMember ? getNodes(['members'])(memberData) : []); }, [memberData, shouldFetchMember]);
    useEffect(function () {
        if (fetchedMembers.length === 1) {
            setCalls({ index: callIndex, memberInfo: fetchedMembers[0] });
        }
    }, [fetchedMembers, callIndex, setCalls]);
    var agent = agentToCall || agentContact;
    var member = memberToCall || (fetchedMembers.length === 1 ? fetchedMembers[0] : null);
    var _e = member || {}, id = _e.id, first_name = _e.first_name, last_name = _e.last_name, preferred_phone_number = _e.preferred_phone_number, dob = _e.dob;
    var _f = agent || {}, firstName = _f.firstName, lastName = _f.lastName, stationPhoneNumber = _f.stationPhoneNumber, skillName = _f.skillName, teamName = _f.teamName;
    var org_name = pathOr('', ['organization', 'name'], member);
    var tpa_name = pathOr('', ['organization', 'third_party_admin', 'name'], member);
    var _g = useState(false), showKeypad = _g[0], setShowKeypad = _g[1];
    var _h = useState(false), isLeaveConfirmOpen = _h[0], setIsLeaveConfirmOpen = _h[1];
    var handleEnd = function () { return endCall(callIndex); };
    var handleLeave = function () {
        setIsLeaveConfirmOpen(false);
        leaveConferenceCall(callIndex);
    };
    return (_jsxs("div", { children: [member && (_jsxs(StyledMember, { children: [_jsxs("div", { style: { display: 'flex', justifyContent: 'space-between' }, children: [_jsxs("span", { style: { fontSize: 14, fontWeight: 600 }, children: [first_name, " ", last_name] }), _jsx(Button, { onClick: function () { return window.open("/members/".concat(id)); }, children: "Member Profile" })] }), _jsxs("span", { children: ["Org: ", org_name, " ", tpa_name ? "- ".concat(tpa_name) : ''] }), _jsx("span", { children: preferred_phone_number || 'Unknown number' }), dob && _jsxs("span", { children: ["DOB: ", dob] })] })), agent && (_jsxs(StyledMember, { children: [_jsx("div", { style: { display: 'flex', justifyContent: 'space-between' }, children: _jsxs("span", { style: { fontSize: 14, fontWeight: 600 }, children: [firstName, " ", lastName] }) }), _jsxs("span", { children: ["Team: ", teamName || 'Unknown'] }), _jsxs("span", { children: ["Phone: ", stationPhoneNumber || 'Unknown number'] }), skillName && _jsxs("span", { children: ["Skill: ", skillName] })] })), !member && !agent && (_jsx(StyledMember, { children: validPhoneNumber ? (_jsxs("div", { style: {
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%'
                    }, children: [_jsx("span", { style: { fontSize: 14, fontWeight: 600 }, children: validPhoneNumber }), _jsx(Button, { onClick: function () {
                                return window.open("/members?phone_search=".concat(validPhoneNumber));
                            }, children: "Search" })] })) : (_jsxs("div", { style: {
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                        width: '100%'
                    }, children: [_jsxs("span", { children: ["ANI: ", ani] }), _jsxs("span", { children: ["DNIS: ", dnis] })] })) })), _jsx("div", { style: { padding: '0 16px 0' }, children: isConferenceInitiated && activeCallsCount >= 2 ? (_jsx(Button, { style: { width: '100%' }, onClick: function () {
                        setConferenceInitiated(false);
                        useCallStore.getState().setShowAdditionalDirectory(false);
                        startConferenceCall();
                    }, children: "Start Conference" })) : (_jsx(Button, { style: { width: '100%' }, disabled: isConferenceActive, onClick: function () {
                        setConferenceInitiated(true);
                        useCallStore.getState().setShowAdditionalDirectory(true);
                    }, children: "Add Party To Call" })) }), _jsx("div", { style: { display: 'flex', gap: 8, padding: 16 }, children: Object.entries((call === null || call === void 0 ? void 0 : call.callControlButton) || {}).map(function (_a) {
                    var key = _a[0], config = _a[1];
                    var action = callActionsConfig[key];
                    if (!action)
                        return null;
                    if (!canShowAction(key, call === null || call === void 0 ? void 0 : call.status)) {
                        return null;
                    }
                    // Determine if button should show active state
                    var isActive = (key === 'hold' && isOnHold) ||
                        (key === 'mute' && isMuted) ||
                        (key === 'transfer' && showAdditionalDirectory);
                    var isDisabled = !config.isEnable ||
                        (key === 'transfer' && isConferenceActive) ||
                        action.disabled;
                    return (_jsxs(CallAction, { onClick: action.onClick, disabled: isDisabled, "$isActive": isActive, style: action.disabled
                            ? {
                                backgroundColor: '#E5E7EB',
                                color: '#FFFFFF',
                                border: 'none',
                                cursor: 'not-allowed',
                                opacity: 1,
                                pointerEvents: 'none'
                            }
                            : undefined, children: [action.icon, _jsx("span", { style: { margin: 0 }, children: action.getText ? action.getText(config) : config.controlText })] }, key));
                }) }), _jsx("div", { style: { padding: '0 16px 8px' }, children: _jsx(Button, { style: { width: '100%' }, btntype: showKeypad ? 'primary' : undefined, onClick: function () { return setShowKeypad(function (v) { return !v; }); }, children: "Keypad" }) }), showKeypad && _jsx(DTMFKeypad, {}), _jsxs(StyledCallFooter, { children: [_jsxs(EndCallButton, { onClick: function () { return handleEnd(); }, children: [_jsx(PhoneOutlined, { rotate: 225 }), _jsx("span", { children: "End" })] }), isConferenceActive && isFirstActiveCall && (_jsxs(LeaveCallButton, { onClick: function () { return setIsLeaveConfirmOpen(true); }, children: [_jsx(LogoutOutlined, {}), _jsx("span", { children: "Leave" })] }))] }), _jsx(Modal, { open: isLeaveConfirmOpen, title: "Leave conference?", okText: "Leave", cancelText: "Cancel", onOk: handleLeave, onCancel: function () { return setIsLeaveConfirmOpen(false); }, children: "The other two callers will remain connected" })] }));
};
export default CallInProgress;
